
import React, { useState } from "react";
import {
  Modal,
  Box,
  Card,
  CardContent,
  CardActions,
  Grid,
  Typography,
  Stack,
  Chip,
  Divider,
  Button,
  IconButton,
} from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import CloseIcon from "@mui/icons-material/Close";
import apiRequest from "../../../apiRequest";

const VehicleDetailModal = ({
  open,
  onClose,
  selectedVehicle,
  onApprove,
  onReject,
}) => {
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState("");

  if (!selectedVehicle) return null;

  const infoCards = [
    { label: "Number Plate", value: selectedVehicle.number_plate },
    { label: "Vehicle Make", value: selectedVehicle.vehicle_make },
    { label: "Vehicle Model", value: selectedVehicle.vehicle_model },
    // { label: "Body Type", value: selectedVehicle.body_type },
    { label: "Vehicle Year", value: selectedVehicle.vehicle_year },
    { label: "Category", value: selectedVehicle.category?.name_en },
    { label: "Sub Category ID", value: selectedVehicle.sub_category_id },
  ];

  const documentCards = [
    { label: "Vehicle Istamara", key: "insurance_photo" },
    { label: "Ownership Photo", key: "ownership_photo" },
    { label: "Tow Operator Certificate", key: "tow_operator_certificate" },
  ];
  const vehiclePhotos =
    selectedVehicle?.documents?.filter((doc) => doc?.type === "vehicle_photo") || [];

  const handleOpenPreview = (image) => {
    setPreviewImage(image);
    setPreviewOpen(true);
  };

  return (
    <>
      <Modal open={open} onClose={onClose}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "90%",
            maxWidth: 780,
            height: "90vh",
            maxHeight: "90vh",
            overflow: "hidden",
          }}
        >
          <Card
            sx={{
              borderRadius: 3,
              boxShadow: 8,
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
              height: "100%",
            }}
          >
            {/* Close button */}
            <IconButton
              onClick={onClose}
              sx={{ position: "absolute", top: 8, right: 8, zIndex: 10 }}
            >
              <CloseIcon />
            </IconButton>

            {/* Card Content */}
            <CardContent sx={{ p: 0, flex: 1, overflowY: "auto" }}>
              {/* Header */}
              <Box
                sx={{
                  px: 4,
                  py: 3,
                  background: "linear-gradient(135deg, #d3f8df 0%, #f4fff7 100%)",
                  borderBottom: "1px solid #e5e7eb",
                }}
              >
                <Typography variant="subtitle2" color="text.secondary">
                  Vehicle Details
                </Typography>
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  justifyContent="space-between"
                  alignItems={{ xs: "flex-start", sm: "center" }}
                  spacing={2}
                  mt={1}
                >
                  <Box>
                    <Typography variant="h5" fontWeight={700}>
                      {selectedVehicle.vehicle_make} {selectedVehicle.vehicle_model}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Plate #{selectedVehicle.number_plate || "-"}
                    </Typography>
                  </Box>
                  <Stack direction="row" spacing={1}>
                    <Chip
                      label={selectedVehicle.status || "Pending"}
                      color={
                        selectedVehicle.status === "approved"
                          ? "success"
                          : selectedVehicle.status === "rejected"
                            ? "error"
                            : "warning"
                      }
                    />
                    <Chip
                      label={`Year ${selectedVehicle.vehicle_year || "-"}`}
                      variant="outlined"
                    />
                  </Stack>
                </Stack>
              </Box>

              {/* Info Cards */}
              <Box sx={{ px: 4, py: 3 }}>
                <Grid container spacing={2}>
                  {infoCards.map((item) => (
                    <Grid item xs={12} sm={6} key={item.label}>
                      <Box
                        sx={{
                          border: "1px solid #edf2f7",
                          borderRadius: 2,
                          p: 2,
                          backgroundColor: "#fafafa",
                        }}
                      >
                        <Typography variant="caption" color="text.secondary">
                          {item.label}
                        </Typography>
                        <Typography fontWeight={600}>{item.value || "-"}</Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>

                <Divider sx={{ my: 3 }} />

                <Typography variant="subtitle2" mb={2}>
                  Documents
                </Typography>
                <Grid container spacing={2}>
                  {documentCards.map((doc) => (
                    <Grid item xs={12} sm={4} key={doc.label}>
                      <Box
                        sx={{
                          border: "1px solid #e5e7eb",
                          borderRadius: 2,
                          p: 2,
                          backgroundColor: "#fff",
                          minHeight: 200,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <Stack spacing={1} alignItems="center">
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            textAlign="center"
                          >
                            {doc.label}
                          </Typography>

                          {selectedVehicle?.[doc.key] ? (
                            <Box
                              component="img"
                              src={`${apiRequest.BASE_URL}/${selectedVehicle[doc.key]}`}
                              sx={{
                                width: "100%",
                                height: 160,
                                objectFit: "cover",
                                borderRadius: 2,
                                border: "1px solid #e5e7eb",
                                boxShadow: "0 4px 12px rgba(15,23,42,0.08)",
                                cursor: "pointer",
                              }}
                              onClick={() =>
                                handleOpenPreview(
                                  `${apiRequest.BASE_URL}/${selectedVehicle[doc.key]}`
                                )
                              }
                            />
                          ) : (
                            <Typography color="text.secondary" fontStyle="italic">
                              No Image
                            </Typography>
                          )}
                        </Stack>
                      </Box>
                    </Grid>
                  ))}
                </Grid>

                <Divider sx={{ my: 3 }} />

                <Typography variant="subtitle2" mb={2}>
                  Vehicle Photos
                </Typography>
                <Grid container spacing={2}>
                  {vehiclePhotos.length ? (
                    vehiclePhotos.map((doc) => (
                      <Grid item xs={12} sm={4} key={doc.id || doc.file_path}>
                        <Box
                          sx={{
                            border: "1px solid #e5e7eb",
                            borderRadius: 2,
                            p: 2,
                            backgroundColor: "#fff",
                            minHeight: 200,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <Box
                            component="img"
                            src={`${apiRequest.BASE_URL}/${doc.file_path}`}
                            sx={{
                              width: "100%",
                              height: 160,
                              objectFit: "cover",
                              borderRadius: 2,
                              border: "1px solid #e5e7eb",
                              boxShadow: "0 4px 12px rgba(15,23,42,0.08)",
                              cursor: "pointer",
                            }}
                            onClick={() =>
                              handleOpenPreview(
                                `${apiRequest.BASE_URL}/${doc.file_path}`
                              )
                            }
                          />
                        </Box>
                      </Grid>
                    ))
                  ) : (
                    <Grid item xs={12}>
                      <Typography color="text.secondary" fontStyle="italic">
                        No Vehicle Photos
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </Box>
            </CardContent>

            {/* Actions */}
            <CardActions
              sx={{
                borderTop: "1px solid #e5e7eb",
                p: 3,
                justifyContent: "flex-end",
                gap: 1.5,
                flexWrap: "wrap",
                bgcolor: "#fff",
              }}
              mt={4} display="flex" justifyContent="flex-end" flexWrap="wrap" gap={2}>

              <MDButton
                variant="gradient"
                color="error"
                // sx={{ mr: 2, minWidth: 140 }}
                sx={{
                  minWidth: 140,
                  fontWeight: 600,
                  borderRadius: 2,
                }}
                onClick={() => onReject(selectedVehicle)}
              >
                Reject
              </MDButton>

              <MDButton
                variant="gradient"
                color="info"
                sx={{
                  minWidth: 140,
                  fontWeight: 600,
                  borderRadius: 2,
                }}
                onClick={() => onApprove(selectedVehicle)}
              >
                Approve
              </MDButton>








            </CardActions>

          </Card>
        </Box>
      </Modal >

      {/* Image Preview */}
      < Modal open={previewOpen} onClose={() => setPreviewOpen(false)}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            maxWidth: "90vw",
            maxHeight: "90vh",
            bgcolor: "background.paper",
            p: 2,
            borderRadius: 2,
            boxShadow: 24,
            overflow: "auto",
          }}
        >
          <Box
            component="img"
            src={previewImage}
            sx={{ width: "100%", height: "auto" }}
          />
          <Box display="flex" justifyContent="flex-end" mt={1}>
            <Button size="small" variant="outlined" onClick={() => setPreviewOpen(false)}>
              Close
            </Button>
          </Box>
        </Box>
      </Modal >
    </>
  );
};

export default VehicleDetailModal;
